import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  CountryIso2Code,
  CountryIso3Code,
  CountryIsoNumber,
  CountryName,
  TranslateService,
} from '@i18n';
import { FormControlValidators } from '../../../shared/utils/form-control-validators/form-control-validators.utils';
import { FormManualAddress } from '../../models/form-manual-address/form-manual-address.interface';
import { AddressTranslationDictionary } from '../../models/i18n/address-translation-dictionary.interface';
import { AddressFormBuilder } from '../address-form-builder/address-form-builder.service';

@Injectable()
export class IEAddressFormBuilder extends AddressFormBuilder {
  protected override readonly formBuilder = inject(UntypedFormBuilder);
  protected override readonly translateService =
    inject<TranslateService<AddressTranslationDictionary>>(TranslateService);

  protected override buildManualAddressFormConfig(
    address?: Partial<FormManualAddress>,
    disabled?: boolean,
  ): Record<keyof FormManualAddress, AbstractControl> {
    return {
      addressLine1: new UntypedFormControl(
        { value: address?.addressLine1, disabled },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate('ADDRESS_LINE_1_ERROR_REQUIRED'),
          ),
          FormControlValidators.restrictedCharacters(
            this.translateService.translate(
              'ADDRESS_LINE_1_ERROR_RESTRICTED_CHARACTERS',
            ),
          ),
          FormControlValidators.maximumLength(
            50,
            this.translateService.translate('ADDRESS_LINE_1_ERROR_MAX_LENGTH', {
              maxLength: 50,
            }),
          ),
        ]),
      ),
      addressLine2: new UntypedFormControl(
        { value: address?.addressLine2, disabled },
        Validators.compose([
          FormControlValidators.restrictedCharacters(
            this.translateService.translate(
              'ADDRESS_LINE_2_ERROR_RESTRICTED_CHARACTERS',
            ),
          ),
          FormControlValidators.maximumLength(
            50,
            this.translateService.translate('ADDRESS_LINE_2_ERROR_MAX_LENGTH', {
              maxLength: 50,
            }),
          ),
        ]),
      ),
      addressLine3: new UntypedFormControl(
        { value: address?.addressLine3, disabled },
        Validators.compose([
          FormControlValidators.restrictedCharacters(
            this.translateService.translate(
              'ADDRESS_LINE_3_ERROR_ALPHABETICAL',
            ),
          ),
          FormControlValidators.maximumLength(
            50,
            this.translateService.translate('ADDRESS_LINE_3_ERROR_MAX_LENGTH', {
              maxLength: 50,
            }),
          ),
        ]),
      ),
      addressLine4: new UntypedFormControl(
        { value: address?.addressLine4, disabled },
        Validators.compose([
          FormControlValidators.restrictedCharacters(
            this.translateService.translate(
              'ADDRESS_LINE_4_ERROR_ALPHABETICAL',
            ),
          ),
          FormControlValidators.maximumLength(
            50,
            this.translateService.translate('ADDRESS_LINE_4_ERROR_MAX_LENGTH', {
              maxLength: 50,
            }),
          ),
        ]),
      ),
      postalCode: new UntypedFormControl(
        { value: address?.postalCode, disabled },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'ADDRESS_POSTAL_CODE_ERROR_REQUIRED',
            ),
          ),
          FormControlValidators.postcode(
            CountryIsoNumber.Ireland,
            this.translateService.translate(
              'ADDRESS_POSTAL_CODE_ERROR_POSTCODE',
            ),
          ),
        ]),
      ),
      countryCode: new UntypedFormControl(CountryIso3Code.Ireland, {
        nonNullable: true,
      }),
      countryIso2: new UntypedFormControl(CountryIso2Code.Ireland, {
        nonNullable: true,
      }),
      countryIso3: new UntypedFormControl(CountryIso3Code.Ireland, {
        nonNullable: true,
      }),
      countryIsoNumber: new UntypedFormControl(CountryIsoNumber.Ireland, {
        nonNullable: true,
      }),
      countryName: new UntypedFormControl(CountryName.Ireland, {
        nonNullable: true,
      }),
    };
  }
}
